import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MenuWorkflowMapper } from '@/lib/MenuWorkflowMapper';
import { localStorageVariables } from '@/types/localStorageVariables';
import { applayoutService, MenuItems } from '@/components/layout/app-layout-menuService';
import { Trans, useTranslation } from 'yardi-react-translation/translationService';

// Initialize menu service
const menuService = new applayoutService();

// Default step sequence for fallback
export const PROGRESS_STEP_SEQUENCE = [] as const;


interface ProgressContextType {
    progressValue: number;
    setProgressValue: (value: number, allowDecrease?: boolean) => void;
    completedSteps: string[];
    currentStep: string | '';
    setCompletedStep: (step: string) => void;
    stepSequence: readonly string[];
}

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

interface ProgressProviderProps {
    children: React.ReactNode;
}

export const ProgressProvider: React.FC<ProgressProviderProps> = ({ children }) => {
    const [progressValue, setProgressValueState] = useState<number>(5); // Default to 5% like Angular
    const [completedSteps, setCompletedSteps] = useState<string[]>([]);
    const [currentStep, setCurrentStep] = useState<string | ''>('');
    const [stepSequence, setStepSequence] = useState<readonly string[]>(PROGRESS_STEP_SEQUENCE);
    const progressValueRef = useRef<number>(progressValue);
    const { t:translate } = useTranslation();
    // Initialize step sequence and completed steps
    useEffect(() => {
        const initializeSteps = async () => {
            try {
                // Get menu items
                const menuItems = await menuService.getMenuItems();
                const activeItems = menuItems.filter(item => !item.Inactive);
                const steps = activeItems.map(item => item.SystemMenuName.toLowerCase());
                
                // Update step sequence
                setStepSequence(steps);
                
                // Get last completed step and current step from localStorage
                const lastCompletedStep = localStorage.getItem('LastCompletedStepPageName');
                const currentMenuStep = localStorage.getItem('MenuWorkflowStep');
                
                console.log('ProgressContext: Initializing steps:', {
                    lastCompletedStep,
                    currentMenuStep,
                    totalSteps: steps.length,
                    steps
                });
                
                if (currentMenuStep) {
                    // Find index of last completed step
                    const currentMenuStepIndex = steps.findIndex(
                        step => step.toLowerCase() === currentMenuStep.toLowerCase()
                    );
                    
                    if (currentMenuStepIndex !== -1) {
                        // Get all steps up to and including last completed step
                        const completedStepsArray = steps.slice(0, currentMenuStepIndex);
                        
                        console.log('ProgressContext: Setting completed steps:', {
                            currentMenuStepIndex,
                            completedStepsArray
                        });
                        
                        // Update state
                        setCompletedSteps(completedStepsArray);
                        setProgressValueState(Math.round(((currentMenuStepIndex) / steps.length) * 100));
                        
                        // Set current step to next step if available
                        if (currentMenuStepIndex  < steps.length) {
                            setCurrentStep(steps[currentMenuStepIndex]);
                        }
                    }
                }
                
                // Dispatch event to notify menu of initialization
                window.dispatchEvent(new CustomEvent('progressInitialized', {
                    detail: {
                        completedSteps: completedSteps,
                        currentStep: currentStep,
                        totalSteps: steps.length
                    }
                }));
                
            } catch (error) {
                console.error('Failed to initialize steps:', error);
            }
        };
        
        initializeSteps();
    }, []);

    // Update ref when state changes
    useEffect(() => {
        progressValueRef.current = progressValue;
    }, [progressValue]);

    const setProgressValue = useCallback((value: number, allowDecrease: boolean = false) => {
        // Enhanced debugging to track progress changes with better caller identification
        const stack = new Error().stack;
        const callerLine = stack?.split('\n')[3] || 'unknown'; // Get the actual caller, not the callback
        const componentMatch = callerLine.match(/(\w+Component|\w+Page|\w+Menu)/) || ['unknown'];
        const componentName = componentMatch[0];
        
        const currentValue = progressValueRef.current; // Use ref to avoid dependency on progressValue
        
        console.log(' ProgressContext: setProgressValue called', {
            newValue: value,
            currentValue: currentValue,
            allowDecrease,
            caller: componentName,
            callerLine: callerLine.trim()
        });

        // ENHANCED DEBUG: Special logging for 100% values to catch the culprit
        if (value === 100) {
            console.warn(' 🚨 PROGRESS 100% DETECTED:', {
                newValue: value,
                currentValue: currentValue,
                caller: componentName,
                stackTrace: stack?.split('\n').slice(1, 6).join('\n'), // Show more stack trace
                timestamp: new Date().toISOString()
            });
        }

        // Ensure progress is between 0 and 100
        const clampedValue = Math.max(0, Math.min(100, value));
        // Ensure minimum of 5% like Angular
        const finalValue = clampedValue === 0 ? 5 : clampedValue;
        
        // Prevent progress from decreasing unless explicitly allowed
        if (!allowDecrease && finalValue < currentValue) {
            console.warn(` Progress decrease prevented: ${currentValue}% → ${finalValue}% (caller: ${componentName})`);
            return;
        }
        
        console.log(` ProgressContext: Setting progress from ${currentValue} to ${finalValue} (caller: ${componentName})`);
        setProgressValueState(finalValue);

        // Update completed steps based on progress value
        const stepIndex = Math.floor((finalValue / 100) * stepSequence.length);
        const completedStepsArray = stepSequence.slice(0, stepIndex) as string[];
        setCompletedSteps(completedStepsArray);
        
        // Set current step
        if (stepIndex < stepSequence.length) {
            setCurrentStep(stepSequence[stepIndex]);
        }
    }, [stepSequence]); // Add stepSequence to dependencies

    const setCompletedStep = useCallback((step: string) => {
        // Find the index of the completed step
        const stepIndex = stepSequence.indexOf(step.toLowerCase());
        
        if (stepIndex !== -1) {
            // Get all steps up to and including the completed step
            const newCompletedSteps = stepSequence.slice(0, stepIndex + 1);
            
            // Update completed steps
            setCompletedSteps(newCompletedSteps);
            
            // Calculate and update progress value
            const progressPercentage = Math.round(((stepIndex + 1) / stepSequence.length) * 100);
            setProgressValueState(progressPercentage);
            
            // Set next step as current if available
            if (stepIndex + 1 < stepSequence.length) {
                setCurrentStep(stepSequence[stepIndex + 1]);
            }
            
            console.log('ProgressContext: Updated completed steps:', {
                completedStep: step,
                newCompletedSteps,
                progressPercentage,
                nextStep: stepIndex + 1 < stepSequence.length ? stepSequence[stepIndex + 1] : 'none'
            });
        } else {
            console.warn('ProgressContext: Attempted to complete unknown step:', step);
        }
    }, [stepSequence]);

    // Initialize from localStorage if available
    useEffect(() => {
        const lastCompletedStep = localStorage.getItem('LastCompletedStepPageName') as string | null;
        if (lastCompletedStep && stepSequence.includes(lastCompletedStep)) {
            const stepIndex = stepSequence.indexOf(lastCompletedStep);
            if (stepIndex !== -1) {
                // Set all steps up to and including the last completed step
                const completedStepsArray = stepSequence.slice(0, stepIndex + 1) as string[];
                setCompletedSteps(completedStepsArray);
                
                // Set current step to the next one
                if (stepIndex + 1 < stepSequence.length) {
                    setCurrentStep(stepSequence[stepIndex + 1]);
                }

                // Set progress value
                const progressPercentage = Math.round(((stepIndex + 1) / stepSequence.length) * 100);
                setProgressValueState(progressPercentage);
            }
        }
    }, [stepSequence]); // Add stepSequence to dependencies

    const contextValue: ProgressContextType = {
        progressValue,
        setProgressValue,
        completedSteps,
        currentStep,
        setCompletedStep,
        stepSequence
    };

    return (
        <ProgressContext.Provider value={contextValue}>
            {children}
        </ProgressContext.Provider>
    );
};

export const useProgress = (): ProgressContextType => {
    const context = useContext(ProgressContext);
    if (!context) {
        throw new Error('useProgress must be used within a ProgressProvider');
    }
    return context;
};

// Keep useGranularProgress hook for backward compatibility
export const useGranularProgress = () => {
    const { setProgressValue, completedSteps, setCompletedStep, stepSequence } = useProgress();
    
    const calculateProgressFromGranularStep = useCallback((
        lastCompletedStepPageName: string,
        applicantStatus?: string
    ) => {
        console.log(' useGranularProgress: Starting calculation for:', lastCompletedStepPageName, 'status:', applicantStatus);
        
        // SPECIAL CASE: Check if user is on pages that use AppLayoutMenu but are outside the main application flow
        // These pages have the circular progress bar but aren't part of the step sequence
        const nonMenuPages = [
            '/ole/guarantordetail',      // MyProfilePage - uses AppLayoutMenu
            '/ole/myaccounts',     // MyAccountPage - uses AppLayoutMenu
            '/ole/notifications',  // NotificationsPage - uses AppLayoutMenu
            '/ole/roommatematching' // RoommateMatchingPage - uses AppLayoutMenu
        ];
        
        const currentPath = window.location.pathname.toLowerCase();
        const isOnNonMenuPage = nonMenuPages.some(page => currentPath.includes(page));
        
        if (isOnNonMenuPage) {
            // Get the current saved progress value from localStorage
            const savedProgress = localStorage.getItem('applicationProgress');
            const currentProgress = savedProgress ? parseInt(savedProgress, 10) : 5;
            const pageName = nonMenuPages.find(page => currentPath.includes(page))?.replace('/ole/', '') || 'non-menu';
            console.log(` useGranularProgress: On ${pageName} page (non-menu), preserving current progress:`, currentProgress);
            return currentProgress; // Return current progress without recalculation
        }
        
        // Handle special case: if no step completed, default to 5%
        if (!lastCompletedStepPageName) {
            console.log(' useGranularProgress: No step provided, defaulting to 5%');
            return 5;
        }
        
        // SPECIAL CASE: Check if user is on screening page (server doesn't set LastCompletedStepPageName to "screening")
        const isOnScreeningPage = window.location.pathname.includes('/ole/screening');
        let effectiveStep = lastCompletedStepPageName;
        
        if (isOnScreeningPage && lastCompletedStepPageName.toLowerCase() === 'applicationcharges') {
            effectiveStep = 'screening';
            console.log(' useGranularProgress: On screening page after payment, treating as screening step');
        }
        
        // ENHANCED: Handle application step with status-based progress
        if (effectiveStep.toLowerCase() === 'application') {
            console.log(' useGranularProgress: Application step detected, calculating status-based progress');
            
            // Check if this is on the application dashboard
            const isOnApplicationDashboard = window.location.pathname.includes('/ole/application');
            
            console.log(' 🔍 Application Step Debug:', {
                effectiveStep,
                applicantStatus: applicantStatus || 'NOT PROVIDED',
                isOnApplicationDashboard,
                currentPath: window.location.pathname
            });
            
            if (isOnApplicationDashboard && applicantStatus) {
                console.log(' ✅ On application dashboard with status - applying status-based progress');
                
                // Status-based progress within application step
                switch (applicantStatus) {
                    case 'Applied':
                        console.log(' ✅ Applied status detected - returning 93% progress');
                        return 93;
                    case 'Approved':
                        console.log(' ✅ Approved status detected - returning 95% progress');
                        return 95;
                    case 'Lease Generated':
                    case 'LeaseGenerated': // Handle both formats
                        console.log(' ✅ Lease Generated status detected - returning 97% progress');
                        return 97;
                    case 'Signed':
                    case 'Lease Signed':
                        console.log(' ✅ Lease Signed status detected - returning 99% progress');
                        return 99;
                    case 'Resident':
                    case 'Lease Counter Signed':
                        console.log(' ✅ Resident/Counter Signed status detected - returning 100% progress');
                        return 100;
                    default:
                        console.log(' ⚠️ Unknown application status, defaulting to 92%:', applicantStatus);
                        return 92;
                }
            } else if (isOnApplicationDashboard) {
                // Fallback when no status available
                console.log(' ⚠️ On application dashboard but no status - capping at 92%');
                return 92;
            } else {
                console.log(' ❌ Not on application dashboard, using normal calculation');
                // Continue to normal step-based calculation below
            }
        }
        
        // Find the step index in our sequence
        const stepIndex = stepSequence.indexOf(lastCompletedStepPageName);
        
        if (stepIndex === -1) {
            console.warn(' useGranularProgress: Unknown step name:', lastCompletedStepPageName);
            console.warn(' useGranularProgress: Available steps:', stepSequence);
            return 5; // Default fallback
        }
        
        // Handle skipped steps (like IDVerification when disabled)
        let effectiveStepIndex = stepIndex;
        
        // If we completed rentaloptions and IDVerification is disabled, 
        // treat it as if we also completed idverification AND getready steps
        if (lastCompletedStepPageName.toLowerCase() === 'rentaloptions') {
            const isIDVerificationEnabled = localStorage.getItem('isOnlineLeasingIDVerifyEnabled') === 'true';
            if (!isIDVerificationEnabled) {
                console.log(' useGranularProgress: IDVerification disabled, advancing past idverification and getready');
                // rentaloptions=2, idverification=3, getready=4
                // So if we completed rentaloptions, we should be at getready (index 4)
                effectiveStepIndex = 4; // getready step index
            }
        }
        
        // Calculate progress: ((effectiveStepIndex + 1) / totalSteps) * 100
        // +1 because we want completed steps, not current step
        let progress = Math.round(((effectiveStepIndex + 1) / stepSequence.length) * 100);
        
        // SPECIAL CASE: Handle "application" step to prevent default 100%
        // When application step is completed, default to 92% instead of 100%
        // This allows status-based calculation to provide more accurate percentages
        if (lastCompletedStepPageName.toLowerCase() === 'application') {
            console.log(' useGranularProgress: Application step in sequence - defaulting to 92% instead of 100%');
            console.log(' useGranularProgress: Status-based calculation will provide accurate percentage if available');
            progress = 92; // Default for application step, overridden by status-based logic above
        }
        
        // Update completed steps based on progress
        if (effectiveStepIndex >= 0) {
            const completedStepsArray = stepSequence.slice(0, effectiveStepIndex + 1) as string[];
            setCompletedStep(stepSequence[effectiveStepIndex]);
        }
        
        return progress;
    }, [setProgressValue, setCompletedStep, stepSequence]);

    const updateProgressFromLastCompletedStep = useCallback((applicantStatus?: string) => {
        const lastCompletedStep = localStorage.getItem('LastCompletedStepPageName') as string | null;
        console.log(' updateProgressFromLastCompletedStep: Reading from localStorage:', lastCompletedStep, 'status:', applicantStatus);
        
        if (!lastCompletedStep) {
            return 5; // Default progress
        }
        
        const calculatedProgress = calculateProgressFromGranularStep(lastCompletedStep, applicantStatus);
        console.log(' updateProgressFromLastCompletedStep: Setting progress to:', calculatedProgress);
        
        setProgressValue(calculatedProgress, false);
        return calculatedProgress;
    }, [calculateProgressFromGranularStep, setProgressValue]);

    return { 
        calculateProgressFromGranularStep, 
        updateProgressFromLastCompletedStep,
        stepSequence
    };
};

// Hook to automatically update progress when API updates LastCompletedStepPageName
export const useApiProgressSync = (applicantStatus?: string) => {
    const { setCompletedStep, stepSequence } = useProgress();
    
    useEffect(() => {
        const lastCompletedStep = localStorage.getItem('LastCompletedStepPageName') as string | null;
        if (lastCompletedStep && stepSequence.includes(lastCompletedStep)) {
            setCompletedStep(lastCompletedStep);
        }
        
        // Update menu workflow
        MenuWorkflowMapper.updateMenuWorkflowStep();
        window.dispatchEvent(new CustomEvent('menuWorkflowStepChanged', {
            detail: { 
                menuWorkflowStep: localStorage.getItem(localStorageVariables.MenuWorkflowStep),
                lastCompletedPage: lastCompletedStep,
                source: 'useApiProgressSync-initial'
            }
        }));
        
        // Listen for API responses that update LastCompletedStepPageName
        const handleApiStepUpdate = (event: CustomEvent) => {
            const lastCompletedStep = localStorage.getItem('LastCompletedStepPageName') as string | null;
            if (lastCompletedStep && stepSequence.includes(lastCompletedStep)) {
                setCompletedStep(lastCompletedStep);
            }
            MenuWorkflowMapper.updateMenuWorkflowStep(event.detail?.applicantStatus);
        };
        
        window.addEventListener('apiStepUpdate', handleApiStepUpdate as EventListener);
        
        return () => {
            window.removeEventListener('apiStepUpdate', handleApiStepUpdate as EventListener);
        };
    }, [setCompletedStep, applicantStatus, stepSequence]);
};

export const useLocationAwareApiProgressSync = (applicantStatus?: string) => {
    const location = useLocation();
    const { stepSequence } = useProgress();
    useApiProgressSync(applicantStatus);

    useEffect(() => {
        // Only update workflow on page refresh or direct URL access
        const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        const isPageRefresh = navigationEntries[0]?.type === 'reload';
        const isDirectAccess = !document.referrer || navigationEntries[0]?.type === 'navigate';

        if (isPageRefresh || (isDirectAccess && !sessionStorage.getItem('recentNavigation'))) {
            MenuWorkflowMapper.updateMenuWorkflowStep(applicantStatus);
            window.dispatchEvent(new CustomEvent('menuWorkflowStepChanged', {
                detail: {
                    menuWorkflowStep: localStorage.getItem(localStorageVariables.MenuWorkflowStep),
                    lastCompletedPage: localStorage.getItem(localStorageVariables.LastCompletedStepPageName),
                    applicantStatus: applicantStatus,
                    source: 'ProgressContext-page-refresh-sync'
                }
            }));
        }

        sessionStorage.setItem('recentNavigation', 'true');
        setTimeout(() => {
            sessionStorage.removeItem('recentNavigation');
        }, 1000);
    }, [location.pathname, applicantStatus, stepSequence]);
};

// Predefined progress values for specific pages (can be customized)
export const PageProgressValues = {
    getstarted: 0,
    dashboard: 10,
    guarantordetail: 20,
    idverification: 30,
    screening: 40,
    individualinfo: 50
} as const; 

// Circular Progress Component
interface CircularProgressProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
  totalStepsCount?: number; // Optional prop to override context value
}

export const CircularStepProgress: React.FC<CircularProgressProps> = ({
  size = 52,
  strokeWidth = 3,
  className = '',
  totalStepsCount
}) => {
  const { stepSequence, progressValue, completedSteps, currentStep } = useProgress();
  
  // Calculate the circle's properties
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  
  // Calculate progress based on completed steps and current step
  // Use props if provided, otherwise fall back to context
  const totalSteps = stepSequence.length;
  
  // Check if we're at the initial step (Get Started)
  const isInitialStep = !localStorage.getItem('LastCompletedStepPageName') || 
                       localStorage.getItem('MenuWorkflowStep') === 'getstarted';
  
  // Show current step (completed + 1) instead of completed steps count
  // If we're at the initial step, show 1 (first step)
  // Otherwise show completedSteps.length + 1, but don't exceed totalSteps
  const displayedCurrentStep = isInitialStep 
    ? 1 
    : Math.min(completedSteps.length + 1, totalSteps);
  
  const currentStepIndex = currentStep ? stepSequence.indexOf(currentStep) : -1;
  // Update effectiveProgress to match the displayed current step
  // Progress should be: (current step / total steps) * 100
  const effectiveProgress = isInitialStep 
    ? 0 
    : Math.min((displayedCurrentStep / totalSteps) * 100, 100);
  
  const offset = circumference - (effectiveProgress / 100) * circumference;

  // For debugging
  useEffect(() => {
    console.log('Progress Debug:', {
      isInitialStep,
      displayedCurrentStep,
      totalSteps,
      currentStep,
      currentStepIndex,
      effectiveProgress,
      progressValue,
      lastCompletedStep: localStorage.getItem('LastCompletedStepPageName'),
      menuWorkflowStep: localStorage.getItem('MenuWorkflowStep')
    });
  }, [isInitialStep, displayedCurrentStep, totalSteps, currentStep, currentStepIndex, effectiveProgress, progressValue]);

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      {/* Background circle */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="text-primary transition-all duration-300 ease-in-out"
          strokeLinecap="round"
        />
      </svg>
      
      {/* Step count text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        {/* <span className="text-[9px] font-medium">
          {displayedCurrentStep} OF {totalSteps}
        </span>
        <span className="text-[9px] font-medium text-gray-600 mt-1">
          STEPS
        </span> */}
        <Trans
            i18nKey="stepsProgress"
            values={{ completed: displayedCurrentStep, total: totalSteps }}
            components={[
            <span className="text-[9px] font-medium" />,
            <span className="text-[9px] font-medium text-gray-600 mt-1" />
            ]}
        />
      </div>
    </div>
  );
};

// Test component to expose test functions
const ProgressTester: React.FC = () => {
    const { stepSequence, setCompletedStep } = useProgress();

    // Test function for browser console debugging
    const testProgressCalculation = useCallback((applicantStatus?: string) => {
        console.log(' Testing Progress Calculation');
        console.log('==============================');
        
        // Get current localStorage values
        const lastCompletedStep = localStorage.getItem('LastCompletedStepPageName') as string | null;
        const isIDVerifyEnabled = localStorage.getItem('isOnlineLeasingIDVerifyEnabled') === 'true';
        
        console.log('Current State:', {
            lastCompletedStepFromAPI: lastCompletedStep,
            applicantStatus: applicantStatus || 'not provided',
            isIDVerificationEnabled: isIDVerifyEnabled,
            stepSequence
        });
        
        if (!lastCompletedStep) {
            console.log(' No LastCompletedStepPageName found in localStorage');
            return;
        }
        
        // Test the new status-based calculation for application step
        if (lastCompletedStep.toLowerCase() === 'application') {
            console.log(' Application step detected - testing status-based progress:');
            
            const testStatuses = ['Applied', 'Approved', 'Lease Generated', 'Signed', 'Resident'];
            testStatuses.forEach(status => {
                const { calculateProgressFromGranularStep } = (window as any).progressTestHelpers || {};
                if (calculateProgressFromGranularStep) {
                    const progress = calculateProgressFromGranularStep(lastCompletedStep, status);
                    console.log(`  ${status}: ${progress}%`);
                }
            });
            
            return;
        }
        
        // Find step index
        const stepIndex = stepSequence.indexOf(lastCompletedStep);
        
        if (stepIndex === -1) {
            console.log(` Step "${lastCompletedStep}" not found in sequence`);
            console.log(' Available steps:', stepSequence.join(', '));
            
            // Special check for petaddons vs petaddon
            if (lastCompletedStep.toLowerCase() === 'petaddons') {
                console.log(' Found "petaddons" step - this should now work correctly!');
            }
            return;
        }
        
        // Calculate effective index (handling skipped steps)
        let effectiveStepIndex = stepIndex;
        if (lastCompletedStep.toLowerCase() === 'rentaloptions' && !isIDVerifyEnabled) {
            effectiveStepIndex = 4; // getready step index (includes landing, floorplans, rentaloptions, idverification, getready)
            console.log(' IDVerification disabled, advancing effective index to getready:', effectiveStepIndex);
            console.log(' Steps auto-completed: landing, floorplans, rentaloptions, idverification(skipped), getready');
        }
        
        const progress = Math.round(((effectiveStepIndex + 1) / stepSequence.length) * 100);
        
        console.log(' Progress Calculation:');
        console.log(`  - Found "${lastCompletedStep}" at index: ${stepIndex}`);
        console.log(`  - Effective index (after skips): ${effectiveStepIndex}`);
        console.log(`  - Calculation: (${effectiveStepIndex + 1} / ${stepSequence.length}) * 100 = ${progress}%`);
        
        // Show what steps are considered completed
        const completedSteps = stepSequence.slice(0, effectiveStepIndex + 1);
        console.log(' Steps considered completed:', completedSteps);
        
        // Special note for petaddons
        if (lastCompletedStep.toLowerCase() === 'petaddons') {
            console.log(' PetAddons step found! This should show correct progress instead of 5%');
            console.log(` Expected progress for petaddons: ${progress}% (step ${stepIndex + 1} of ${stepSequence.length})`);
        }
        
        return progress;
    }, [stepSequence]);

    // Specific test for petaddons progress calculation
    const testPetAddonsProgress = useCallback(() => {
        console.log(' Testing PetAddons Progress Calculation');
        console.log('========================================');
        
        // Save current state
        const originalStep = localStorage.getItem('LastCompletedStepPageName');
        
        // Test petaddons specifically
        localStorage.setItem('LastCompletedStepPageName', 'petaddons');
        
        const stepIndex = stepSequence.indexOf('petaddons' as string);
        
        if (stepIndex === -1) {
            console.log(' FAILED: petaddons not found in sequence');
            console.log(' Available steps:', stepSequence.join(', '));
        } else {    
            const progress = Math.round(((stepIndex + 1) / stepSequence.length) * 100);
            console.log(' SUCCESS: petaddons found at index:', stepIndex);
            console.log(` Calculated progress: (${stepIndex + 1} / ${stepSequence.length}) * 100 = ${progress}%`);
            console.log(' This should replace the 5% default when LastCompletedStepPageName is "petaddons"');
            
            // Trigger progress recalculation
            window.dispatchEvent(new CustomEvent('localStorageUpdated'));
        }
        
        // Restore original state
        if (originalStep) {
            localStorage.setItem('LastCompletedStepPageName', originalStep);
        } else {
            localStorage.removeItem('LastCompletedStepPageName');
        }
        
        return stepIndex !== -1;
    }, [stepSequence]);

    // NEW: Test function for PetAddons -> AlmostThere navigation scenario
    const testPetAddonsToAlmostThereSkip = useCallback(() => {
        console.log(' Testing PetAddons -> AlmostThere Skip Scenario');
        console.log('=================================================');
        
        // Save current state
        const originalStep = localStorage.getItem('LastCompletedStepPageName');
        const originalMenuStep = localStorage.getItem('MenuWorkflowStep');
        const originalExtraAddonsFlag = localStorage.getItem('isExtraAddonsEnabled');
        
        console.log(' This test simulates when user completes PetAddons but ExtraAddons is skipped');
        console.log(' (happens when there are no rentable items available for the property)');
        
        // Simulate the scenario: petaddons completed, MenuWorkflowStep set to UploadDocument, ExtraAddons disabled
        localStorage.setItem('LastCompletedStepPageName', 'petaddons');
        localStorage.setItem('MenuWorkflowStep', 'UploadDocument');
        localStorage.setItem('isExtraAddonsEnabled', 'false');
        
        console.log(' Set simulation state:', {
            LastCompletedStepPageName: 'petaddons',
            MenuWorkflowStep: 'UploadDocument',
            isExtraAddonsEnabled: 'false'
        });
        
        // Test the progress calculation
        const { calculateProgressFromGranularStep } = (window as any).progressTestHelpers || {};
        if (calculateProgressFromGranularStep) {
            const progress = calculateProgressFromGranularStep('petaddons' as string);
            console.log(' Calculated progress for petaddons (with ExtraAddons skip):', progress + '%');
            
            // Expected: should be at extraaddons step (index 10) = 73%
            const expectedProgress = Math.round((11 / stepSequence.length) * 100);
            console.log(' Expected progress (skipping extraaddons):', expectedProgress + '%');
            
            if (progress === expectedProgress) {
                console.log(' SUCCESS: Progress correctly calculated with ExtraAddons skip!');
                console.log(' The isExtraAddonsEnabled flag is working correctly');
            } else {
                console.log(' FAILED: Progress not calculated correctly');
                console.log(' The skip detection logic may need adjustment');
            }
        } else {
            // Trigger progress recalculation manually
            window.dispatchEvent(new CustomEvent('localStorageUpdated'));
            console.log(' Triggered progress recalculation event');
        }
        
        // Restore original state
        if (originalStep) {
            localStorage.setItem('LastCompletedStepPageName', originalStep);
        } else {
            localStorage.removeItem('LastCompletedStepPageName');
        }
        
        if (originalMenuStep) {
            localStorage.setItem('MenuWorkflowStep', originalMenuStep);
        } else {
            localStorage.removeItem('MenuWorkflowStep');
        }
        
        if (originalExtraAddonsFlag) {
            localStorage.setItem('isExtraAddonsEnabled', originalExtraAddonsFlag);
        } else {
            localStorage.removeItem('isExtraAddonsEnabled');
        }
        
        console.log(' Restored original state');
        return true;
    }, [stepSequence]);

    // NEW: Test function to verify the scenario where user manually navigates to /almostthere
    // after completing a later step (like reviewdocuments)
    const testManualNavigationScenario = useCallback(() => {
        console.log(' Testing Manual Navigation to /almostthere Scenario');
        console.log('====================================================');
        
        // Save current state
        const originalStep = localStorage.getItem('LastCompletedStepPageName');
        const originalMenuStep = localStorage.getItem('MenuWorkflowStep');
        const originalUrl = window.location.href;
        
        console.log(' This test simulates: User completed reviewdocuments, then manually navigates to /almostthere');
        console.log(' Expected: Menu should show reviewdocuments progress, NOT be affected by current URL');
        
        // Simulate the scenario: reviewdocuments completed
        localStorage.setItem('LastCompletedStepPageName', 'reviewdocuments');
        
        // Simulate URL change to almostthere (this is what browser navigation would do)
        console.log(' Simulating navigation to /almostthere URL...');
        
        // Test the progress calculation with reviewdocuments step
        const reviewdocumentsIndex = stepSequence.findIndex(
            step => step.toLowerCase() === 'reviewdocuments'
        );
        
        if (reviewdocumentsIndex !== -1) {
            const expectedProgress = Math.round(((reviewdocumentsIndex + 1) / stepSequence.length) * 100);
            console.log(' Expected progress for reviewdocuments:', expectedProgress + '%');
            console.log(' reviewdocuments is at index:', reviewdocumentsIndex);
            
            // Trigger progress recalculation
            window.dispatchEvent(new CustomEvent('localStorageUpdated'));
            
            // The key test: progress calculation should NOT be affected by URL containing /almostthere
            // because the logic only applies when LastCompletedStepPageName === 'petaddons'
            
            setTimeout(() => {
                console.log(' Test Complete: Progress calculation should show reviewdocuments progress');
                console.log(' The ExtraAddons skip logic should NOT apply because LastCompletedStepPageName ≠ "petaddons"');
                console.log(' Menu should show: Documents milestone as completed, Fees as current');
                
                // Restore original state
                if (originalStep) {
                    localStorage.setItem('LastCompletedStepPageName', originalStep);
                } else {
                    localStorage.removeItem('LastCompletedStepPageName');
                }
                
                if (originalMenuStep) {
                    localStorage.setItem('MenuWorkflowStep', originalMenuStep);
                } else {
                    localStorage.removeItem('MenuWorkflowStep');
                }
                
                console.log(' Restored original state');
            }, 100);
        } else {
            console.log(' FAILED: reviewdocuments not found in step sequence');
        }
        
        return true;
    }, [stepSequence]);

    // NEW: Test function for Application Status Progress Calculation
    const testApplicationStatusProgress = useCallback(() => {
        console.log(' Testing Application Status Progress Calculation');
        console.log('===========================================');
        
        // Save current state
        const originalStep = localStorage.getItem('LastCompletedStepPageName');
        
        // Set to application step
        localStorage.setItem('LastCompletedStepPageName', 'application');
        
        console.log(' Testing different statuses when LastCompletedStepPageName = "application":');
        
        // Test all possible statuses with expected results
        const statusTests = [
            { status: 'Applied', expected: 93 },
            { status: 'Approved', expected: 95 },
            { status: 'Lease Generated', expected: 97 },
            { status: 'LeaseGenerated', expected: 97 },
            { status: 'Signed', expected: 99 },
            { status: 'Lease Signed', expected: 99 },
            { status: 'Resident', expected: 100 },
            { status: 'Lease Counter Signed', expected: 100 },
            { status: 'Unknown Status', expected: 92 }
        ];
        
        statusTests.forEach(test => {
            console.log(`  ${test.status}: ${test.expected}% (expected)`);
        });
        
        console.log('\n💡 Usage: Navigate to /ole/application page and this will show different progress based on your status');
        console.log('💡 Status progression: Applied(93%) → Approved(95%) → Lease Generated(97%) → Signed(99%) → Resident(100%)');
        console.log('💡 To test: Change your applicant status and watch the progress bar update automatically');
        
        // Trigger a progress recalculation to see current behavior
        console.log('\n🔄 Triggering progress recalculation...');
        window.dispatchEvent(new CustomEvent('localStorageUpdated'));
        
        // Restore original state
        if (originalStep) {
            localStorage.setItem('LastCompletedStepPageName', originalStep);
        } else {
            localStorage.removeItem('LastCompletedStepPageName');
        }
        
        console.log(' Restored original LastCompletedStepPageName state');
        return true;
    }, []);

    // Make test functions available globally
    useEffect(() => {
        if (typeof window !== 'undefined') {
            (window as any).testProgressCalculation = testProgressCalculation;
            (window as any).testPetAddonsProgress = testPetAddonsProgress;
            (window as any).testPetAddonsToAlmostThereSkip = testPetAddonsToAlmostThereSkip;
            (window as any).testManualNavigationScenario = testManualNavigationScenario;
            (window as any).testApplicationStatusProgress = testApplicationStatusProgress;
            (window as any).recalculateProgress = () => {
                console.log(' Manually triggering progress recalculation...');
                window.dispatchEvent(new CustomEvent('localStorageUpdated'));
                return testProgressCalculation();
            };
        }
    }, [testProgressCalculation, testPetAddonsProgress, testPetAddonsToAlmostThereSkip, testManualNavigationScenario, testApplicationStatusProgress]);

    return null;
};

// Export the test component
export const ProgressTesterComponent = ProgressTester; 